import { createMemo, For, Show, Suspense } from "solid-js";
import { msg } from "@lingui/core/macro";
import { createRoute, useLocation } from "@tanstack/solid-router";
import { useNavigate } from "@/lib/u-router";
import { appRoute } from "./route";
import { safeRedirectTarget, useSession } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { PageContainer } from "@/components/ui/page-container";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@bso/shared/solid";
import type { ThemesRecord } from "@bso/powersync";
import { getThemesQuery, queryKeys, updateGlobalThemeMutation } from "@bso/shared";
import { LOCALE_DEFINITIONS } from "@bso/config/locales";
import { ClearLocalDataButton } from "./profile/clear-local-data-button";
import { EmailVerificationBanner } from "./profile/email-verification-banner";
import {
  Cpu,
  Globe,
  Keyboard,
  Loader2,
  LogOut,
  Mail,
  Megaphone,
  Palette,
  Settings,
  Sparkles,
  Trash2,
  Type,
  User,
} from "lucide-solid";
import { useTypography } from "@/providers/typography-provider";
import { useI18n } from "@/providers/i18n-provider";
import { useSignOut } from "@/providers/boot-manager";
import { SyncIndicator } from "@/components/sync-indicator";
import { SyncStatusPill } from "@/components/sync-status-pill";
import { StatsSection } from "./profile/stats-section";
import { SessionsSection } from "./profile/sessions-section";
import { ChangePasswordSection } from "./profile/change-password-section";
import { MfaSection } from "./profile/mfa-section";
import { AppLockSection } from "./profile/app-lock-section";
import { ConnectedAccountsSection } from "./profile/connected-accounts-section";
import { DangerZoneSection } from "./profile/danger-zone-section";
import { NotificationPreferencesSection } from "./profile/notification-preferences-section";
import { Logo } from "@/components/ui/logo";
import { SettingsLinkCard } from "@/components/ui/settings-link-card";
import { SettingsRowButton } from "@/components/ui/settings-row-button";

function ProfilePage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const { userId } = session;
  const { t, locale } = useI18n();
  const { promptSignOut, isChecking, isSigningOut } = useSignOut();

  const themes = createPowerSyncQuery<ThemesRecord>({
    queryKey: () => queryKeys.themes.catalog(),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getThemesQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle()),
    suspense: false,
  });

  const handleUpdateTheme = async (themeId: string) => {
    const dbInstance = drizzle();
    const uid = userId();
    if (!dbInstance || !uid) {
      return;
    }
    try {
      await updateGlobalThemeMutation(dbInstance, uid, themeId);
    } catch (err) {
      console.error("Error updating theme:", err);
    }
  };

  const isAdmin = createMemo(() => session.data?.user.role === "admin");

  return (
    <div class="flex flex-1 flex-col min-h-0 overflow-hidden bg-background">
      <PageSubHeader title={t(msg`Settings`)} rightElement={<SyncIndicator />} />

      <PageContainer maxWidth="2xl" class="space-y-6" containerClass="min-h-0">
        <div class="space-y-6">
          {/* Account / Profile */}
          <Show
            when={!session.isPending}
            fallback={
              <Card>
                <CardContent class="space-y-4 pt-6">
                  <div class="flex items-center gap-4">
                    <Skeleton class="h-12 w-12 rounded-full" />
                    <div class="space-y-2">
                      <Skeleton class="h-4 w-32" />
                      <Skeleton class="h-3 w-48" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            }
          >
            <Show
              when={session.isAuthenticated()}
              fallback={
                <Card class="border-primary/20 bg-primary/5">
                  <CardContent class="flex flex-col items-center p-8 text-center space-y-4">
                    <div class="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <User class="h-8 w-8" />
                    </div>
                    <div class="space-y-1">
                      <CardTitle class="text-xl font-bold">{t(msg`Guest Mode`)}</CardTitle>
                      <p class="text-sm text-muted-foreground">
                        {t(msg`Your tasks are saved locally. Sign in to sync across devices.`)}
                      </p>
                      <SyncStatusPill class="mt-2" />
                    </div>
                    <Button
                      onClick={() =>
                        void navigate({
                          to: "/login",
                          search: { redirect: safeRedirectTarget(location().pathname) },
                        })
                      }
                      class="w-full sm:w-auto"
                    >
                      {t(msg`Sign In / Sign Up`)}
                    </Button>
                  </CardContent>
                </Card>
              }
            >
              <Show when={session.data}>
                {(data) => (
                  <Card>
                    <CardHeader>
                      <CardTitle>{t(msg`Account Information`)}</CardTitle>
                    </CardHeader>
                    <CardContent class="space-y-4">
                      <div class="flex items-center gap-4">
                        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-primary/20 text-primary font-bold text-lg">
                          {data().user.name?.charAt(0).toUpperCase() ??
                            data().user.email.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p class="font-medium text-lg">{data().user.name ?? t(msg`User`)}</p>
                          <p class="text-sm text-muted-foreground">{data().user.email}</p>
                          <SyncStatusPill class="mt-2 !items-start" />
                        </div>
                      </div>
                      <EmailVerificationBanner emailVerified={data().user.emailVerified} />
                      <Separator />
                      <div class="flex items-center justify-between">
                        <span class="text-sm text-muted-foreground">{t(msg`Role`)}</span>
                        <Badge variant="secondary" class="capitalize">
                          {data().user.role}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </Show>
            </Show>
          </Show>

          {/* Stats Section */}
          <Suspense
            fallback={
              <Card>
                <CardContent class="pt-6">
                  <Skeleton class="h-28 w-full rounded-xl" />
                </CardContent>
              </Card>
            }
          >
            <StatsSection userId={userId()} />
          </Suspense>

          {/* Core Settings Links */}
          <div class="grid gap-4 sm:grid-cols-2">
            <SettingsLinkCard
              title={t(msg`Language`)}
              to="/language"
              icon={Globe}
              indicator={
                <span class="text-2xl">
                  {LOCALE_DEFINITIONS.find((d) => d.code === locale())?.flag ?? "🌐"}
                </span>
              }
              label={LOCALE_DEFINITIONS.find((d) => d.code === locale())?.label ?? "English"}
            />

            <SettingsLinkCard
              title={t(msg`Typography`)}
              to="/profile/fonts"
              icon={Type}
              indicator={<span class="text-2xl font-serif">Aa</span>}
              // eslint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n)
              label={useTypography().fontFamilyName() ?? "Default System"}
            />

            <SettingsLinkCard
              title={t(msg`Keyboard Shortcuts`)}
              to="/profile/shortcuts"
              icon={Keyboard}
              indicator={<span class="text-2xl font-mono">⌘?</span>}
              label={t(msg`Interactive Guide`)}
            />

            <SettingsLinkCard
              title={t(msg`Trash`)}
              to="/profile/trash"
              icon={Trash2}
              indicator={<Trash2 class="h-6 w-6 text-muted-foreground" />}
              label={t(msg`Restore deleted notes`)}
            />
          </div>

          {/* Themes */}
          <Card>
            <CardHeader class="flex flex-row items-center justify-between gap-2">
              <div class="flex items-center gap-2">
                <Palette class="h-5 w-5 text-primary" />
                <CardTitle>{t(msg`Global Theme`)}</CardTitle>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void navigate({ to: "/themes" })}
                class="text-xs text-primary hover:text-primary hover:bg-primary/10"
              >
                {t(msg`View All`)}
                <Sparkles class="ml-2 h-3 w-3" />
              </Button>
            </CardHeader>
            <CardContent>
              <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Show
                  when={!themes.isPending}
                  fallback={
                    <>
                      <Skeleton class="h-20 w-full rounded-lg" />
                      <Skeleton class="h-20 w-full rounded-lg" />
                      <Skeleton class="h-20 w-full rounded-lg" />
                    </>
                  }
                >
                  <For each={themes.data}>
                    {(theme) => (
                      <button
                        type="button"
                        class="group relative flex flex-col items-center gap-2 rounded-lg border border-border p-3 transition-all hover:border-primary/50 hover:bg-muted/50"
                        onClick={() => void handleUpdateTheme(theme.id)}
                      >
                        <div
                          class="h-10 w-full rounded-md border border-border shadow-inner"
                          style={{ "background-color": theme.background ?? "" }}
                        >
                          <div
                            class="m-1.5 h-3 w-1/2 rounded-full"
                            style={{ "background-color": theme.accent ?? "" }}
                          />
                        </div>
                        <span class="text-[10px] font-medium truncate w-full text-center">
                          {theme.name}
                        </span>
                      </button>
                    )}
                  </For>
                </Show>
              </div>
            </CardContent>
          </Card>

          {/* Utility Links */}
          <div class="space-y-3">
            <SettingsRowButton label={t(msg`System Info`)} to="/debug" icon={Cpu} />

            <Show when={isAdmin()}>
              <SettingsRowButton
                label={t(msg`Font Administration`)}
                to="/admin/fonts"
                icon={Settings}
              />

              <SettingsRowButton
                label={t(msg`Theme Administration`)}
                to="/admin/themes"
                icon={Palette}
              />

              <SettingsRowButton label={t(msg`Email Templates`)} to="/admin/email" icon={Mail} />

              <SettingsRowButton
                label={t(msg`Email Campaigns`)}
                to="/admin/email/campaigns"
                icon={Megaphone}
              />
            </Show>
          </div>

          {/* Sessions Section */}
          <Show when={session.isAuthenticated()}>
            <SessionsSection currentSessionId={session.data?.session.id} />
          </Show>

          {/* Cambio de contraseña */}
          <Show when={session.isAuthenticated()}>
            <ChangePasswordSection />
          </Show>

          {/* MFA (TOTP) self-service (#18) */}
          <Show when={session.isAuthenticated()}>
            <MfaSection enabled={session.data?.user.twoFactorEnabled ?? false} />
          </Show>

          {/* Biometric app-lock (OPT-IN, off by default) — native-mobile only.
              Self-gates via isTauriRuntime(): renders nothing on web/desktop. */}
          <AppLockSection />

          {/* Cuentas conectadas (Google / Apple) */}
          <Show when={session.isAuthenticated()}>
            <ConnectedAccountsSection />
          </Show>

          {/* Preferencias de notificación por canal (campaña #11) */}
          <Show when={session.isAuthenticated()}>
            <NotificationPreferencesSection />
          </Show>

          {/* Danger Zone: eliminación de cuenta con periodo de gracia */}
          <Show when={session.isAuthenticated()}>
            <DangerZoneSection />
          </Show>

          {/* Destructive Actions */}
          <div class="pt-4">
            <Show
              when={session.isAuthenticated()}
              fallback={<ClearLocalDataButton db={db} userId={userId} />}
            >
              <Button
                variant="destructive"
                class="w-full rounded-2xl gap-2 h-12"
                onClick={() => {
                  promptSignOut();
                }}
                disabled={isChecking() || isSigningOut()}
              >
                <Show when={isChecking() || isSigningOut()} fallback={<LogOut class="h-5 w-5" />}>
                  <Loader2 class="h-5 w-5 animate-spin" />
                </Show>
                {t(msg`Sign out`)}
              </Button>
            </Show>
          </div>

          {/* App Meta */}
          <div class="pt-4 pb-8 space-y-6">
            <Separator />
            <div class="flex flex-col items-center justify-center text-center space-y-4">
              <Logo size="lg" />
              {/* eslint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n) */}
              <p class="text-sm text-muted-foreground">Version 0.1.0-beta</p>
              <div class="flex items-center gap-4 text-xs font-medium text-muted-foreground">
                <button
                  type="button"
                  class="hover:text-primary transition-colors bg-transparent border-none p-0 cursor-pointer"
                >
                  {t(msg`Privacy`)}
                </button>
                <span class="h-1 w-1 rounded-full bg-border" />
                <button
                  type="button"
                  class="hover:text-primary transition-colors bg-transparent border-none p-0 cursor-pointer"
                >
                  {t(msg`Terms`)}
                </button>
                <span class="h-1 w-1 rounded-full bg-border" />
                <button
                  type="button"
                  class="hover:text-primary transition-colors bg-transparent border-none p-0 cursor-pointer"
                >
                  {t(msg`Support`)}
                </button>
              </div>
              <p class="text-[10px] text-muted-foreground max-w-[200px]">
                {t(
                  "Designed and developed with ❤️ by the Bible Strong Offline team. © {{year}} All rights reserved.",
                  { year: 2026 },
                )}
              </p>
            </div>
          </div>
        </div>
      </PageContainer>
    </div>
  );
}

export const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile",
  validateSearch: (search: Record<string, unknown>): { linked?: string } => ({
    linked: typeof search.linked === "string" ? search.linked : undefined,
  }),
  component: ProfilePage,
});
